import React, { useState } from 'react';
import { ChevronDown, ChevronLeft, KeyRound, LogOut, User, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brand, logoConfig } from '../../config/brand';
import { avatarColor } from '../ui/Avatar';
import { useVerwaltungsMenue, isActivePath, menuIcons, type MenueAbschnitt } from './verwaltungsMenue';
import { istInApp } from '../../utils/eingebettet';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const CLOSED_SECTIONS_KEY = 'sidebar-closed-sections';

/** Zustand der Abschnitte: eine Liste der zugeklappten Titel, damit ein neuer Abschnitt offen startet. */
const readClosedSections = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(CLOSED_SECTIONS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { sections, countFor } = useVerwaltungsMenue();

  const [closedSections, setClosedSections] = useState<Record<string, boolean>>(readClosedSections);
  const isSectionClosed = (section: MenueAbschnitt): boolean =>
    section.label ? (closedSections[section.label] ?? section.defaultClosed ?? false) : false;
  const toggleSection = (section: MenueAbschnitt) => {
    if (!section.label) return;
    const next = { ...closedSections, [section.label]: !isSectionClosed(section) };
    setClosedSections(next);
    try { localStorage.setItem(CLOSED_SECTIONS_KEY, JSON.stringify(next)); } catch { /* kein Speicher, kein Merken */ }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside
        className={`sidebar ${isOpen ? 'sidebar-open' : ''} ${collapsed ? 'sidebar-collapsed' : ''}`}
        aria-label="Hauptnavigation"
      >
        <div className="sidebar-header">
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2 sidebar-brand-link" onClick={onClose}>
              {logoConfig.logo && (
                <img
                  src={logoConfig.logo}
                  alt={brand.shortName}
                  style={{ width: 24, height: 24 }}
                  className="sidebar-logo"
                />
              )}
              <h2>{brand.shortName}</h2>
            </Link>
          )}
          <button className="sidebar-close" onClick={onClose} aria-label="Menü schließen">
            <X size={18} aria-hidden />
          </button>
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Menü ausklappen' : 'Menü einklappen'}
            title={collapsed ? 'Menü ausklappen' : 'Menü einklappen'}
          >
            <ChevronLeft
              size={16}
              aria-hidden
              style={{ transition: 'transform 0.2s', transform: collapsed ? 'rotate(180deg)' : 'none' }}
            />
          </button>
        </div>
        <nav className="sidebar-nav">
          {sections.map((section, idx) => {
            // Im schmalen Modus gibt es keine Titel zum Klappen: dort stehen alle Symbole.
            const closed = !collapsed && isSectionClosed(section);
            const closedCount = closed ? section.items.reduce((sum, i) => sum + countFor(i.path), 0) : 0;
            const activeInside = section.items.some(i => isActivePath(i.path, location.pathname));
            return (
              <div key={section.label ?? `section-${idx}`} className="sidebar-section">
                {section.label && !collapsed && (
                  <button
                    type="button"
                    className={`sidebar-section-header${activeInside && closed ? ' sidebar-section-header--active' : ''}`}
                    onClick={() => toggleSection(section)}
                    aria-expanded={!closed}
                  >
                    <span>{section.label}</span>
                    {closedCount > 0 && (
                      <span className="sidebar-badge sidebar-badge--section">{closedCount > 99 ? '99+' : closedCount}</span>
                    )}
                    <ChevronDown size={14} aria-hidden className={`sidebar-section-chevron${closed ? ' sidebar-section-chevron--closed' : ''}`} />
                  </button>
                )}
                {section.label && collapsed && idx > 0 && (
                  <div className="sidebar-section-divider" />
                )}
                {!closed && section.items.map((item) => {
                  const Icon = menuIcons[item.path];
                  const count = countFor(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`sidebar-link ${isActivePath(item.path, location.pathname) ? 'active' : ''}`}
                      onClick={onClose}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="sidebar-icon">
                        {Icon && <Icon className="sidebar-icon-svg" strokeWidth={1.75} aria-hidden />}
                        {collapsed && count > 0 && <span className="sidebar-badge-dot" />}
                      </span>
                      {!collapsed && <span className="sidebar-label">{item.label}</span>}
                      {!collapsed && count > 0 && (
                        <span className="sidebar-badge">{count > 99 ? '99+' : count}</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User Menu */}
        <div className="sidebar-footer">
          <div className="user-section">
            <div
              className="user-info"
              onClick={() => setShowUserMenu(!showUserMenu)}
              role="button"
              tabIndex={0}
              aria-expanded={showUserMenu}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowUserMenu(!showUserMenu); }}}
            >
              <div className="user-avatar" style={{ backgroundColor: avatarColor(user?.firstName, user?.lastName) }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              {!collapsed && (
                <>
                  <div className="user-details">
                    <div className="user-name">
                      {user?.firstName} {user?.lastName}
                    </div>
                    <div className="user-role">
                      {user?.role === 'ADMIN' ? 'Administrator' : user?.role === 'MODERATOR' ? 'Moderator' : 'Nutzer'}
                    </div>
                  </div>
                  <div className="user-menu-toggle">
                    <ChevronDown
                      size={14}
                      aria-hidden
                      style={{ transition: 'transform 0.2s', transform: showUserMenu ? 'rotate(180deg)' : 'none' }}
                    />
                  </div>
                </>
              )}
            </div>

            {showUserMenu && (
              <div className="user-menu">
                <Link
                  to="/profile"
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); onClose(); }}
                >
                  <span className="user-menu-icon">
                    <User className="sidebar-icon-svg" strokeWidth={1.75} aria-hidden />
                  </span>
                  {!collapsed && <span>Profil</span>}
                </Link>
                {!istInApp() && (<>
                <Link
                  to="/change-password"
                  className="user-menu-item"
                  onClick={() => { setShowUserMenu(false); onClose(); }}
                >
                  <span className="user-menu-icon"><KeyRound className="sidebar-icon-svg" strokeWidth={1.75} aria-hidden /></span>
                  {!collapsed && <span>Passwort ändern</span>}
                </Link>
                <button
                  className="user-menu-item"
                  onClick={handleLogout}
                >
                  <span className="user-menu-icon"><LogOut className="sidebar-icon-svg" strokeWidth={1.75} aria-hidden /></span>
                  {!collapsed && <span>Abmelden</span>}
                </button>
                </>)}
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
